import { getItemDef } from "../items/db.js";
import { WILDLIFE_SPECIES, type WildlifeSpecies } from "../sim/wildlife.js";
import { MAX_LEVEL, XP } from "../meta/progress.js";
import type { RaidResult } from "../protocol/messages.js";
import { getTrader } from "./traders.js";

/**
 * What the valley wants from you. A quest is a trader, a goal, a reward and
 * optionally the quest before it; short chains, and the first of every chain is
 * available from account creation.
 *
 * Two transactions, neither during a raid: `applyRaidToQuests` (the
 * settlement — kills and extractions) and `applyQuestTurnIn` (the counter —
 * fetch quests advance here and nowhere else; the settlement used to eat items
 * the player meant to sell). Both are pure, total and deterministic, so
 * progress cannot be duplicated by disconnecting. `import type { RaidResult }`
 * keeps one spelling of "extracted | died | mia" without a runtime cycle.
 */

// ---------------------------------------------------------------------------
// Content vocabulary
// ---------------------------------------------------------------------------

/**
 * What has to happen for a quest to tick over. `count` is the FULL target, so
 * the log can draw "7 / 12".
 */
export type QuestGoal =
  /** Kill Wilderer. Counted whatever the raid's outcome was. */
  | { t: "killScavs"; count: number }
  /** Kill other PMCs. Same. */
  | { t: "killPmcs"; count: number }
  /**
   * Kill BOSSES — its own goal, since a boss is at most one per raid and
   * folding him into the Wilderer tally would make the quest trivial or
   * endless.
   */
  | { t: "killBoss"; count: number }
  /**
   * Get out alive `count` times. `extractId` narrows to one exit; every
   * authored quest omits it (generated extract ids change with the map).
   */
  | { t: "extract"; count: number; extractId?: string }
  /**
   * Carry `count` units out and hand them over AT THE COUNTER. Handover
   * consumes the items and is the one goal the settlement does not touch: the
   * player picks the instances, `applyQuestTurnIn` does the arithmetic. Nothing
   * is ever taken from a stash the player did not offer.
   */
  | { t: "bringItems"; defId: string; count: number }
  /**
   * Shoot `count` of ONE species, whatever the raid's outcome. Per species,
   * since the three are three different hunts.
   */
  | { t: "killAnimals"; species: WildlifeSpecies; count: number };

export interface QuestDef {
  id: string;
  /** Who hands it out. Must resolve through `getTrader`. */
  traderId: string;
  /** German, Tirolean, and short enough for a log entry. */
  name: string;
  description: string;
  goal: QuestGoal;
  /**
   * For `bringItems`: the goal item spawns as a world item at this address every
   * raid, on any map whose address book carries it. A street and number rather
   * than a position because the map is generated per raid. The generator
   * guarantees the building a walk-in interior with the item inside.
   */
  spawnAt?: { street: string; housenumber: string };
  rewardEuros: number;
  rewardItems?: { defId: string; count: number }[];
  /**
   * Experience on completion; absent = `XP.questDefault`. Not derived from
   * `rewardEuros` — money and xp are different axes.
   */
  rewardXp?: number;
  /**
   * Reputation with this quest's own trader; absent = `QUEST_REP_DEFAULT`. The
   * currency that does not travel. Never stored — standing is derived
   * (`economy/loyalty.ts`), so changing this re-derives every profile.
   */
  rewardRep?: number;
  /**
   * Account level this quest appears at; absent = 1. A gated quest is not
   * active until reached — not in the log, accrues nothing.
   */
  minLevel?: number;
  /**
   * Unlocks when the named quest (same trader) is done. Absent on a chain's
   * first quest.
   */
  after?: string;
}

/** One quest's progress on one profile. The whole of a profile's quest state. */
export interface QuestState {
  id: string;
  /** Units of `goal.count` achieved so far. Never negative, never above it. */
  progress: number;
  done: boolean;
}

/**
 * Default rep for a quest without `rewardRep`. Lives here because
 * `economy/loyalty.ts` imports this file; `LOYALTY.questDefault` aliases it.
 */
export const QUEST_REP_DEFAULT = 30;

// ---------------------------------------------------------------------------
// The quests
// ---------------------------------------------------------------------------

export const QUESTS: readonly QuestDef[] = [
  // --- Hegemeister Ferdl: the shooting chain -------------------------------
  {
    id: "hege_wilderer",
    traderId: "hegemeister",
    name: "Poachers in the Preserve",
    description:
      "Since lockdown began, strangers with shotguns have been roaming my preserve. I want four of them gone.",
    goal: { t: "killScavs", count: 4 },
    rewardEuros: 800,
  },
  {
    id: "hege_revierdruck",
    traderId: "hegemeister",
    name: "Pressure on the Preserve",
    description:
      "Four was a start. Twelve more, and perhaps they will stop crossing the stream.",
    goal: { t: "killScavs", count: 12 },
    rewardEuros: 2200,
    rewardItems: [{ defId: "ammo_8x57_sp", count: 20 }],
    after: "hege_wilderer",
  },
  {
    id: "hege_klingen",
    traderId: "hegemeister",
    name: "Foreign Blades",
    description:
      "Every one of them carries a knife, and not one is theirs. Bring me two — I know which kitchens they came from.",
    goal: { t: "bringItems", defId: "jagdmesser", count: 2 },
    rewardEuros: 1400,
    rewardItems: [{ defId: "red_dot", count: 1 }],
    after: "hege_revierdruck",
  },
  // The end of the gamekeeper's chain and the only boss hunt — four links
  // deep so "there is one more, and he is not like the others" lands last.
  {
    id: "hege_jaeger",
    traderId: "hegemeister",
    name: "The Other Hunter",
    description:
      "There is one working my high ground as though he inherited it, and the poachers up there call him the Hunter. He wears a Gamsbart he never shot. End him, and I will not ask how.",
    goal: { t: "killBoss", count: 1 },
    rewardEuros: 1500,
    // Four times an ordinary task: one raid in a dozen offers the chance.
    rewardXp: 800,
    after: "hege_klingen",
  },

  // --- Wirtin Rosa: the Gasthaus chain, plus one on its own -----------------
  {
    id: "wirtin_speis",
    traderId: "wirtin",
    name: "The Pantry Is Empty",
    description:
      "I cook for everyone still here, and there is nothing left hanging in the cellar. Four slabs of bacon, and dumplings are back on the menu.",
    goal: { t: "bringItems", defId: "speck", count: 4 },
    rewardEuros: 600,
  },
  {
    id: "wirtin_stammtisch",
    traderId: "wirtin",
    name: "The Regulars' Table",
    description:
      "People are eating again, but not talking yet. Three bottles of fruit brandy, and the corner table will fill itself.",
    goal: { t: "bringItems", defId: "schnaps", count: 3 },
    rewardEuros: 900,
    rewardItems: [{ defId: "med_erstehilfe", count: 1 }],
    after: "wirtin_speis",
  },
  {
    id: "wirtin_heimkehr",
    traderId: "wirtin",
    name: "Homecoming",
    description:
      "Many go out and few return. Come through my door alive three times, and next time I will believe you.",
    goal: { t: "extract", count: 3 },
    rewardEuros: 700,
    rewardItems: [{ defId: "med_sanikasten", count: 1 }],
  },

  // Rosa's standing order — the other end of Graggy's trade. No `after`; a
  // quest on its own, like `wirtin_heimkehr`.
  {
    id: "wirtin_wildbret",
    traderId: "wirtin",
    name: "Game for the Kitchen",
    description:
      "Nobody has brought me game since the road closed, and there is only so much bacon a person can serve. Three haunches, and there is venison on the board again.",
    goal: { t: "bringItems", defId: "meat_venison", count: 3 },
    rewardEuros: 400,
  },

  // --- Krämer Alois: the business chain ------------------------------------
  {
    id: "kraemer_draht",
    traderId: "kraemer",
    name: "Copper for the Merchant",
    description:
      "There is a coil of copper wire in every shed in the village. Bring me three, and I will not ask which sheds they came from.",
    goal: { t: "bringItems", defId: "kupferdraht", count: 3 },
    rewardEuros: 500,
  },
  {
    id: "kraemer_konkurrenz",
    traderId: "kraemer",
    name: "Unwelcome Competition",
    description:
      "Two others are clearing out my warehouse, and they are not poachers. You had better not try talking to them.",
    goal: { t: "killPmcs", count: 2 },
    rewardEuros: 1600,
    rewardItems: [{ defId: "vest_loden", count: 1 }],
    after: "kraemer_draht",
  },

  // --- Graggy: woodcraft, patience and proof you made it home ---------------
  // The bottle stands first: the introduction to the trade-in counter, and a
  // task whose answer is a place on the map rather than a number to grind.
  {
    id: "graggy_obstler",
    traderId: "graggy",
    name: "The Branded Bottle",
    description:
      "Before the lockdown I left a bottle of my own Obstler at Unterdorf 42 — my brand is burned into the cork. Bring it home, and mind you bring it unopened.",
    goal: { t: "bringItems", defId: "graggy_obstler", count: 1 },
    spawnAt: { street: "Unterdorf", housenumber: "42" },
    rewardEuros: 1200,
  },
  {
    id: "graggy_spuren",
    traderId: "graggy",
    name: "Tracks in the Woods",
    description:
      "The strangers swagger around and leave more than footprints. Five fewer, and the wildlife will settle down again.",
    goal: { t: "killScavs", count: 5 },
    rewardEuros: 900,
    rewardItems: [{ defId: "ammo_8x57_sp", count: 20 }],
  },
  {
    id: "graggy_heimweg",
    traderId: "graggy",
    name: "The Way Home",
    description:
      "Plenty of people can shoot. Go in twice and come back alive twice — then we can talk about hunting.",
    goal: { t: "extract", count: 2 },
    rewardEuros: 1200,
    rewardItems: [{ defId: "pack_jagd", count: 1 }],
    after: "graggy_spuren",
  },
  {
    id: "graggy_klingen",
    traderId: "graggy",
    name: "Test of Blades",
    description:
      "A serviceable knife says more about its owner than any uniform. Bring me three from the woods.",
    goal: { t: "bringItems", defId: "jagdmesser", count: 3 },
    rewardEuros: 1800,
    rewardItems: [{ defId: "scope_4x", count: 1 }],
    after: "graggy_heimweg",
  },

  // --- Graggy's second chain: the hunt. Hares in the lower fields, a roebuck
  // on the forest edge, chamois on the high ground — three habitats, the whole
  // map. Runs BESIDE his first chain.
  {
    id: "graggy_hasen",
    traderId: "graggy",
    name: "Rabbit Season",
    description:
      "The hares are into the winter kale on the lower fields, and there will be nothing left by the thaw. Three of them, and take the meat — I have no use for it.",
    goal: { t: "killAnimals", species: "hare", count: 3 },
    rewardEuros: 220,
  },
  {
    id: "graggy_rehbock",
    traderId: "graggy",
    name: "The Roebuck",
    description:
      "There is a buck working the edge of the wood at dusk. One clean shot, and mind the birds — everything on this hillside will know where you are standing.",
    goal: { t: "killAnimals", species: "deer", count: 1 },
    rewardEuros: 350,
    // The hunting load for the Eastern rifle, which is what he would take a
    // roebuck with himself. Twenty rounds is a season.
    rewardItems: [{ defId: "ammo_762_hp", count: 20 }],
    after: "graggy_hasen",
  },
  {
    id: "graggy_gams",
    traderId: "graggy",
    name: "Chamois",
    description:
      "Now go up. Two gams off the high ground, and bring the Krickel down with you — anyone can shoot a deer in a meadow.",
    goal: { t: "killAnimals", species: "chamois", count: 2 },
    rewardEuros: 600,
    after: "graggy_rehbock",
  },

  // --- El Gato: proving his weapons work outside the shop -----------------
  {
    id: "el_gato_feldtest",
    traderId: "el-gato",
    name: "Field Test",
    description:
      "Targets on a range prove nothing. Drop two armed rivals, and I will know you can handle what I sell you.",
    goal: { t: "killPmcs", count: 2 },
    rewardEuros: 1100,
    rewardItems: [{ defId: "vest_gendarm", count: 1 }],
  },
];

const QUEST_BY_ID: ReadonlyMap<string, QuestDef> = new Map(QUESTS.map((q) => [q.id, q]));

/** Non-throwing lookup — quest ids come out of a stored profile. */
export function getQuest(id: string): QuestDef | null {
  return QUEST_BY_ID.get(id) ?? null;
}

/** Every quest this trader hands out, in `QUESTS` order. */
export function questsForTrader(traderId: string): QuestDef[] {
  return QUESTS.filter((q) => q.traderId === traderId);
}

// ---------------------------------------------------------------------------
// Settlement
// ---------------------------------------------------------------------------

/** Everything a settlement needs to know about the raid that just ended. */
export interface RaidQuestOutcome {
  result: RaidResult;
  /** Which exit, when `result` is "extracted". `null` otherwise. */
  extractId: string | null;
  scavKills: number;
  pmcKills: number;
  /** Bosses killed. OPTIONAL and 0 when absent, so every caller written before
   *  bosses existed settles exactly as it always did. */
  bossKills?: number;
  /** The account's level for `QuestDef.minLevel`. Optional, 1 when absent. */
  level?: number;
  /**
   * Animals shot this raid, by species. Optional; absent = none, so older
   * callers settle unchanged. `Partial` — the goal reads `?? 0`.
   */
  animalKills?: Partial<Record<WildlifeSpecies, number>>;
}

export interface QuestSettlementResult {
  /** The FULL updated set, in `QUESTS` order. */
  states: QuestState[];
  /** Ids whose progress moved at this settlement — what the summary lists. */
  advanced: string[];
  /** Euro owed for quests that were COMPLETED here. */
  rewardEuros: number;
  /** Items owed for the same. The server mints and places them. */
  rewardItems: { defId: string; count: number }[];
  /**
   * Experience owed: `questRewardXp` summed over quests NEWLY completed here,
   * into `RaidProgressInput.questXp` — settled in the same transaction as the
   * euros.
   */
  rewardXp: number;
}

/**
 * Settles one raid against one profile's quests. Pure, total, deterministic.
 * The rules, in order:
 *  1. states for quests that no longer exist are dropped; progress is clamped
 *     into [0, count] and full progress counts as done;
 *  2. the ACTIVE set is computed once before anything advances (not done, at
 *     least `minLevel`, `after` done), so a quest completed by this settlement
 *     does not feed its successor until the next one — the successor is
 *     materialized at progress 0 so it shows in the log;
 *  3. kill goals advance on every result (dying does not void your kills);
 *  4. extract goals advance by 1 on "extracted", at the named exit if any;
 *  5. handover goals do not advance here at all (`applyQuestTurnIn`);
 *  6. a quest reaching `count` is done and pays euros, items and xp; only
 *     NEWLY done quests pay.
 * `catalog` lets tests use synthetic quests; callers pass two arguments.
 */
export function applyRaidToQuests(
  states: readonly QuestState[],
  outcome: RaidQuestOutcome,
  catalog: readonly QuestDef[] = QUESTS,
): QuestSettlementResult {
  const byId = new Map<string, QuestDef>(catalog.map((q) => [q.id, q]));

  // --- 1. Normalize what came in -------------------------------------------
  const live = normalizeStates(states, byId);

  // --- 2. The active set, once ---------------------------------------------
  const level = asLevel(outcome.level);
  const active: QuestDef[] = [];
  for (const q of catalog) {
    const existing = live.get(q.id);
    if (existing !== undefined && existing.done) continue;
    if (level < (q.minLevel ?? 1)) continue;
    if (q.after !== undefined) {
      const pre = live.get(q.after);
      if (pre === undefined || !pre.done) continue;
    }
    if (existing === undefined) live.set(q.id, { id: q.id, progress: 0, done: false });
    active.push(q);
  }

  // --- 3..6. Advance -------------------------------------------------------
  const extracted = outcome.result === "extracted";
  const scavKills = asCount(outcome.scavKills);
  const pmcKills = asCount(outcome.pmcKills);
  const bossKills = asCount(outcome.bossKills);

  const advanced: string[] = [];
  let rewardEuros = 0;
  let rewardXp = 0;
  const rewardItems = new Map<string, number>();

  for (const q of active) {
    const state = live.get(q.id) as QuestState;
    const goal = q.goal;
    let progress = state.progress;

    switch (goal.t) {
      case "killScavs":
        progress += scavKills;
        break;
      case "killPmcs":
        progress += pmcKills;
        break;
      case "killBoss":
        progress += bossKills;
        break;
      case "killAnimals":
        // Sanitized here because only the goal knows which key it wants;
        // `progress + NaN` would slip past the clamps as a stored NaN.
        progress += asCount(outcome.animalKills?.[goal.species]);
        break;
      case "extract":
        if (extracted && (goal.extractId === undefined || goal.extractId === outcome.extractId)) {
          progress += 1;
        }
        break;
      case "bringItems":
        // Rule 5: handed over at the counter, by hand. See `applyQuestTurnIn`.
        break;
    }

    if (progress > goal.count) progress = goal.count;
    if (progress === state.progress) continue;

    const done = progress >= goal.count;
    live.set(q.id, { id: q.id, progress, done });
    advanced.push(q.id);
    if (!done) continue;

    rewardEuros += q.rewardEuros;
    rewardXp += questRewardXp(q);
    for (const r of q.rewardItems ?? []) {
      rewardItems.set(r.defId, (rewardItems.get(r.defId) ?? 0) + r.count);
    }
  }

  return {
    states: finishStates(live, catalog, level),
    advanced,
    rewardEuros,
    rewardItems: [...rewardItems].map(([defId, count]) => ({ defId, count })),
    rewardXp,
  };
}

/**
 * What finishing `def` pays in xp — its own `rewardXp` or the default. One
 * function for the settlement and the counter.
 */
export function questRewardXp(def: QuestDef): number {
  const authored = def.rewardXp;
  if (typeof authored !== "number" || !Number.isFinite(authored) || authored < 0) {
    return XP.questDefault;
  }
  return Math.floor(authored);
}

/**
 * What one finished quest is worth in standing — its own `rewardRep` or the
 * default. 30 against `TIER_REP`'s 50/150/400: two errands is an Acquaintance,
 * a whole chain is most of a Regular, the rest is bought over the counter.
 * The default lives here because `loyalty.ts` imports this file.
 */
export function questRep(def: QuestDef): number {
  const authored = def.rewardRep;
  if (typeof authored !== "number" || !Number.isFinite(authored) || authored < 0) {
    return QUEST_REP_DEFAULT;
  }
  return Math.floor(authored);
}

// ---------------------------------------------------------------------------
// The shared half of both transactions
// ---------------------------------------------------------------------------

/**
 * Step 1 of both transactions: the stored states made safe — unknown quests
 * dropped, duplicate ids keep the first, progress clamped, full progress
 * counts as done. Both must agree to the letter, so neither owns a copy.
 */
function normalizeStates(
  states: readonly QuestState[],
  byId: ReadonlyMap<string, QuestDef>,
): Map<string, QuestState> {
  const live = new Map<string, QuestState>();
  for (const s of states) {
    const def = byId.get(s.id);
    if (def === undefined) continue;
    if (live.has(s.id)) continue;
    const progress = clampProgress(s.progress, def.goal.count);
    live.set(s.id, {
      id: s.id,
      progress,
      done: s.done === true || progress >= def.goal.count,
    });
  }
  return live;
}

/**
 * The last step of both transactions: materialize whatever the new done-flags
 * unlocked (at progress 0, not active during this transaction) and return the
 * set in catalogue order. `level` gates materialization too; a caller that
 * does not know it passes 1 and leaves gated quests to the next settlement.
 */
function finishStates(
  live: Map<string, QuestState>,
  catalog: readonly QuestDef[],
  level: number,
): QuestState[] {
  for (const q of catalog) {
    if (live.has(q.id)) continue;
    if (level < (q.minLevel ?? 1)) continue;
    if (q.after !== undefined) {
      const pre = live.get(q.after);
      if (pre === undefined || !pre.done) continue;
    }
    live.set(q.id, { id: q.id, progress: 0, done: false });
  }

  const out: QuestState[] = [];
  for (const q of catalog) {
    const state = live.get(q.id);
    if (state !== undefined) out.push(state);
  }
  return out;
}

/** A stored progress value, made safe: integral, non-negative, capped. */
function clampProgress(n: unknown, count: number): number {
  const v = asCount(n);
  return v > count ? count : v;
}

/** A wire/stored counter, made safe: integral and non-negative. */
function asCount(n: unknown): number {
  if (typeof n !== "number" || !Number.isFinite(n) || n <= 0) return 0;
  return Math.floor(n);
}

/** An account level made safe: integral, at least 1. Absent is 1. */
function asLevel(n: unknown): number {
  if (typeof n !== "number" || !Number.isFinite(n) || n < 1) return 1;
  return Math.floor(n);
}

// ---------------------------------------------------------------------------
// The trade-in
// ---------------------------------------------------------------------------

/**
 * What a turn-in did, or why not. The failure half carries a reason and no
 * states (nothing changed). Reasons in check order: unknown, wrong-goal (not a
 * fetch quest), not-given, done, nothing (nothing handed over or needed).
 */
export type QuestTurnInResult =
  | {
      ok: true;
      /** The FULL updated set, in catalogue order — same as a settlement's. */
      states: QuestState[];
      /** Units the quest actually took. Never more than the remainder. */
      accepted: number;
      /** True when this turn-in FINISHED the quest, and only then. */
      completed: boolean;
      /** Euro owed. Zero unless `completed`. */
      rewardEuros: number;
      /** Items owed. Empty unless `completed`. The server mints and places them. */
      rewardItems: { defId: string; count: number }[];
    }
  | { ok: false; reason: "unknown" | "wrong-goal" | "not-given" | "done" | "nothing" };

/**
 * Hands `offeredUnits` over against ONE fetch quest. Pure, total,
 * deterministic. The caller owns the ITEMS (finds, checks, consumes, counts);
 * this owns the quest-state arithmetic and never sees an instance. That split
 * keeps double-paying impossible: the arithmetic never advances past
 * `goal.count` or pays a done quest, so the worst a confused client gets is
 * "nothing". `offeredUnits` is sanitized like every counter. `catalog` as in
 * the settlement.
 */
export function applyQuestTurnIn(
  states: readonly QuestState[],
  questId: string,
  offeredUnits: number,
  catalog: readonly QuestDef[] = QUESTS,
): QuestTurnInResult {
  const byId = new Map<string, QuestDef>(catalog.map((q) => [q.id, q]));

  const def = byId.get(questId);
  if (def === undefined) return { ok: false, reason: "unknown" };
  const goal = def.goal;
  if (goal.t !== "bringItems") return { ok: false, reason: "wrong-goal" };

  const live = normalizeStates(states, byId);
  // A state exists exactly for the quests given, so absence answers "may I hand
  // this in?".
  const state = live.get(questId);
  if (state === undefined) return { ok: false, reason: "not-given" };
  if (state.done) return { ok: false, reason: "done" };

  const remaining = goal.count - state.progress;
  const offered = asCount(offeredUnits);
  const accepted = offered < remaining ? offered : remaining;
  if (accepted <= 0) return { ok: false, reason: "nothing" };

  const progress = state.progress + accepted;
  const completed = progress >= goal.count;
  live.set(questId, { id: questId, progress, done: completed });

  return {
    ok: true,
    // Level 1: a trade-in knows nothing about the account, so a level-gated
    // successor is left for the next settlement.
    states: finishStates(live, catalog, 1),
    accepted,
    completed,
    rewardEuros: completed ? def.rewardEuros : 0,
    // A COPY of the def's list, never the list itself: the caller is about to
    // mint items from it, and content is not something a transaction may edit.
    rewardItems: completed ? (def.rewardItems ?? []).map((r) => ({ ...r })) : [],
  };
}

/**
 * Every distinct address a quest item spawns at, deduped on street + number in
 * catalogue order.
 */
export function questSpawnAddresses(
  catalog: readonly QuestDef[] = QUESTS,
): { street: string; housenumber: string }[] {
  const out: { street: string; housenumber: string }[] = [];
  const seen = new Set<string>();
  for (const q of catalog) {
    const at = q.spawnAt;
    if (at === undefined) continue;
    // Keyed on the pair: street names contain spaces, so gluing the halves
    // would collapse two different doors.
    const key = JSON.stringify([at.street, at.housenumber]);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ street: at.street, housenumber: at.housenumber });
  }
  return out;
}

/** The quest states a brand-new profile starts with: every chain's first
 *  quest, at zero. Same shape the settlement would materialize. */
export function createStarterQuests(): QuestState[] {
  return QUESTS.filter((q) => q.after === undefined).map((q) => ({
    id: q.id,
    progress: 0,
    done: false,
  }));
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Structural check for the quests, in `validateItemDb`'s shape. A unit test and
 * a boot assertion.
 */
export function validateQuests(): string[] {
  const problems: string[] = [];
  const seen = new Set<string>();

  for (const q of QUESTS) {
    const where = `quest "${q.id}"`;
    if (q.id.length === 0) problems.push("a quest has an empty id");
    if (seen.has(q.id)) problems.push(`duplicate quest id "${q.id}"`);
    seen.add(q.id);
    if (QUEST_BY_ID.get(q.id) !== q) problems.push(`${where} is not the one the registry holds`);

    if (q.name.length === 0) problems.push(`${where} has an empty name`);
    if (q.description.length === 0) problems.push(`${where} has an empty description`);
    if (getTrader(q.traderId) === null) problems.push(`${where} names unknown trader "${q.traderId}"`);

    const goal = q.goal;
    if (!Number.isInteger(goal.count) || goal.count < 1) {
      problems.push(`${where} has goal count ${goal.count} (must be an integer >= 1)`);
    }
    if (goal.t === "bringItems" && getItemDef(goal.defId) === null) {
      problems.push(`${where} asks for unknown item "${goal.defId}"`);
    }
    if (goal.t === "extract" && goal.extractId !== undefined && goal.extractId.length === 0) {
      problems.push(`${where} has an empty extractId`);
    }
    // Only catches a species REMOVED from the roster while a quest still names
    // it.
    if (goal.t === "killAnimals" && !WILDLIFE_SPECIES.includes(goal.species)) {
      problems.push(`${where} hunts unknown species "${goal.species}"`);
    }

    // A spawn address is a fetch quest's arrangement with the map generator;
    // half an address is a door nobody can find.
    const spawnAt = q.spawnAt;
    if (spawnAt !== undefined) {
      if (goal.t !== "bringItems") {
        problems.push(`${where} has a spawnAt but its goal is "${goal.t}"`);
      }
      if (spawnAt.street.length === 0) problems.push(`${where} has an empty spawnAt street`);
      if (spawnAt.housenumber.length === 0) {
        problems.push(`${where} has an empty spawnAt housenumber`);
      }
    }

    if (!Number.isInteger(q.rewardEuros) || q.rewardEuros < 0) {
      problems.push(`${where} has a bad rewardEuros`);
    }
    if (q.rewardXp !== undefined && (!Number.isInteger(q.rewardXp) || q.rewardXp < 0)) {
      problems.push(`${where} has a bad rewardXp`);
    }
    // Rep is summed, so a fraction is fine (`questRep` floors it). Negative is
    // not: a quest that COSTS standing could walk a player back down a tier.
    if (q.rewardRep !== undefined && (!Number.isFinite(q.rewardRep) || q.rewardRep < 0)) {
      problems.push(`${where} has a bad rewardRep`);
    }
    // A gate above the ladder is a quest nobody can ever be given — the same
    // rule `validateTraders` applies to a shelf line.
    if (
      q.minLevel !== undefined &&
      (!Number.isInteger(q.minLevel) || q.minLevel < 1 || q.minLevel > MAX_LEVEL)
    ) {
      problems.push(
        `${where} is gated at level ${q.minLevel} (must be an integer in 1..${MAX_LEVEL})`,
      );
    }
    for (const r of q.rewardItems ?? []) {
      if (getItemDef(r.defId) === null) problems.push(`${where} rewards unknown item "${r.defId}"`);
      if (!Number.isInteger(r.count) || r.count < 1) {
        problems.push(`${where} rewards a bad count of "${r.defId}"`);
      }
    }

    // --- The chain ---------------------------------------------------------
    if (q.after === undefined) continue;
    const pre = QUEST_BY_ID.get(q.after);
    if (pre === undefined) {
      problems.push(`${where} follows unknown quest "${q.after}"`);
      continue;
    }
    // A chain that crosses traders would put one man's quest behind another
    // man's counter, which no quest log can explain.
    if (pre.traderId !== q.traderId) {
      problems.push(`${where} follows "${q.after}", which belongs to trader "${pre.traderId}"`);
    }
    // A cycle is a quest nobody can ever start; walk the chain and say so.
    const visited = new Set<string>([q.id]);
    let cursor: QuestDef | undefined = pre;
    while (cursor !== undefined) {
      if (visited.has(cursor.id)) {
        problems.push(`${where} is part of an unreachable cycle through "${cursor.id}"`);
        break;
      }
      visited.add(cursor.id);
      cursor = cursor.after === undefined ? undefined : QUEST_BY_ID.get(cursor.after);
    }
  }

  return problems;
}
